import { useCallback, useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import {
  computeCanonicalIndicators,
  readExpectedTotals,
  type ExpectedTotals,
} from '@/lib/canonicalIndicators';
import { parseMensalidades, type ParserDiagnostics } from '@/lib/parseMensalidades';
import { validateTransactions, type ValidationResult } from '@/lib/validateTransactions';
import type { CanonicalIndicators } from '@/types/Aggregates';
import type { Transaction } from '@/types/Transaction';

export type LoadStatus = 'idle' | 'loading' | 'success' | 'error';
export type LoadSource = 'auto' | 'manual' | 'drop';

export interface MensalidadesState {
  status: LoadStatus;
  /** Como o ultimo carregamento foi iniciado. null antes do primeiro load. */
  source: LoadSource | null;
  transactions: Transaction[];
  diagnostics: ParserDiagnostics | null;
  expected: ExpectedTotals | null;
  indicators: CanonicalIndicators | null;
  validation: ValidationResult | null;
  /** WorkBook completo do XLSX — partilhado com hooks de Inventario / ApmAccount (F4)
   * para evitar re-fetch e re-parse. null se status !== 'success'. */
  workbook: XLSX.WorkBook | null;
  /** Mensagem human-readable do erro, se status === 'error'. */
  error: string | null;
  /** mtime do .xlsx em modo auto, file.lastModified em manual/drop. null antes. */
  lastUpdated: Date | null;
  /** URL/nome da origem (debug). */
  sourceLabel: string | null;
}

const INITIAL_STATE: MensalidadesState = {
  status: 'idle',
  source: null,
  transactions: [],
  diagnostics: null,
  expected: null,
  indicators: null,
  validation: null,
  workbook: null,
  error: null,
  lastUpdated: null,
  sourceLabel: null,
};

function resolveXlsxUrl(): string {
  const fromEnv = import.meta.env.VITE_XLSX_URL;
  if (fromEnv) return fromEnv;
  if (typeof document !== 'undefined') {
    const meta = document.querySelector<HTMLMetaElement>('meta[name="xlsx-url"]');
    if (meta?.content) return meta.content;
  }
  return './data/AIOBI_Gestor_Plataformas_APM.xlsx';
}

interface ParsedResult {
  workbook: XLSX.WorkBook;
  transactions: Transaction[];
  diagnostics: ParserDiagnostics;
  expected: ExpectedTotals;
  indicators: CanonicalIndicators;
  validation: ValidationResult;
}

function parseWorkbook(buffer: ArrayBuffer): ParsedResult {
  const wb = XLSX.read(buffer, { type: 'array', cellDates: true });
  const { transactions, diagnostics } = parseMensalidades(wb);
  const expected = readExpectedTotals(wb);
  const indicators = computeCanonicalIndicators(transactions, expected);
  const validation = validateTransactions(transactions);
  return { workbook: wb, transactions, diagnostics, expected, indicators, validation };
}

/**
 * Hook central para carregar/expor os dados de Mensalidades.
 *
 * 3 modos:
 *  - auto: fetch do URL (resolvido por VITE_XLSX_URL > meta tag > default)
 *  - manual: file picker via loadFromFile(file)
 *  - drop: drag&drop via loadFromFile(file)
 *
 * State exposto e reactivo a re-renders. useMemo nos calculos pesados via
 * dependencia em transactions/expected.
 *
 * Boot: arranca em modo auto ao montar (auto-load = true por defeito).
 */
export function useMensalidadesData(options: { autoLoad?: boolean } = {}): MensalidadesData {
  const { autoLoad = true } = options;
  const [state, setState] = useState<MensalidadesState>(INITIAL_STATE);

  const loadFromUrl = useCallback(async (url: string, source: LoadSource = 'auto') => {
    setState((s) => ({ ...s, status: 'loading', source, sourceLabel: url, error: null }));
    try {
      const t0 = performance.now();
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status} ao carregar ${url}`);
      const buffer = await response.arrayBuffer();
      const lastModifiedHeader = response.headers.get('Last-Modified');
      const lastUpdated = lastModifiedHeader ? new Date(lastModifiedHeader) : new Date();
      const parsed = parseWorkbook(buffer);
      const elapsed = Math.round(performance.now() - t0);
      /* eslint-disable no-console */
      console.groupCollapsed(
        `%c[AIOBI] ${parsed.transactions.length} transacoes carregadas em ${elapsed}ms (source: ${source})`,
        'color:#1F4E79;font-weight:600',
      );
      console.info('Origem:', url);
      console.info('Diagnostics:', parsed.diagnostics);
      console.info('Validacao F = E*1,22:', {
        total: parsed.validation.total,
        passed: parsed.validation.passed,
        violations: parsed.validation.violations.length,
      });
      console.info('Last-Modified:', lastUpdated.toISOString());
      /* eslint-enable no-console */
      setState({
        status: 'success',
        source,
        transactions: parsed.transactions,
        diagnostics: parsed.diagnostics,
        expected: parsed.expected,
        indicators: parsed.indicators,
        validation: parsed.validation,
        workbook: parsed.workbook,
        error: null,
        lastUpdated,
        sourceLabel: url,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setState((s) => ({ ...s, status: 'error', error: message }));
    }
  }, []);

  const loadFromFile = useCallback(async (file: File, source: LoadSource = 'manual') => {
    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      setState((s) => ({
        ...s,
        status: 'error',
        error: `Ficheiro nao suportado: "${file.name}". Apenas .xlsx e aceite.`,
      }));
      return;
    }
    setState((s) => ({ ...s, status: 'loading', source, sourceLabel: file.name, error: null }));
    try {
      const buffer = await file.arrayBuffer();
      const lastUpdated = new Date(file.lastModified);
      const parsed = parseWorkbook(buffer);
      setState({
        status: 'success',
        source,
        transactions: parsed.transactions,
        diagnostics: parsed.diagnostics,
        expected: parsed.expected,
        indicators: parsed.indicators,
        validation: parsed.validation,
        workbook: parsed.workbook,
        error: null,
        lastUpdated,
        sourceLabel: file.name,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setState((s) => ({ ...s, status: 'error', error: message }));
    }
  }, []);

  const loadAuto = useCallback(() => loadFromUrl(resolveXlsxUrl(), 'auto'), [loadFromUrl]);

  const reload = useCallback(() => {
    // Reload mantem o source anterior se conhecido; senao volta a auto.
    if (state.source === 'auto' || state.source === null) {
      void loadAuto();
    } else {
      // Em modo manual/drop precisa-se de re-prompt pelo file picker (caller decide).
      void loadAuto();
    }
  }, [loadAuto, state.source]);

  const dismissError = useCallback(() => {
    setState((s) => ({ ...s, status: 'idle', error: null }));
  }, []);

  useEffect(() => {
    if (autoLoad) void loadAuto();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return useMemo(
    () => ({ state, loadAuto, loadFromFile, reload, dismissError }),
    [state, loadAuto, loadFromFile, reload, dismissError],
  );
}

export interface MensalidadesData {
  state: MensalidadesState;
  loadAuto: () => Promise<void>;
  loadFromFile: (file: File, source?: LoadSource) => Promise<void>;
  reload: () => void;
  dismissError: () => void;
}
